import { useState, useEffect, useRef } from 'react';
import { ASSETS } from '@/constants/assets';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const oficinaLinks = [
  { label: 'Cilindros Hidráulicos', to: '/recuperacao-cilindros-hidraulicos' },
  { label: 'Bombas Hidráulicas', to: '/recuperacao-bombas-hidraulicas' },
  { label: 'Motores Hidráulicos', to: '/recuperacao-motores-hidraulicos' },
  { label: 'Redutores Industriais', to: '/recuperacao-redutores-industriais' },
  { label: 'Gruas e Equipamentos Florestais', to: '/recuperacao-gruas-e-equipamentos-florestais' },
  { label: 'Equipamentos para Celulose', to: '/recuperacao-equipamentos-papel-celulose' },
];

const duraflowLinks = [
  { label: 'Mangueiras Hidráulicas', to: '/mangueiras-hidraulicas' },
  { label: 'Bombas Hidráulicas', to: '/bombas-hidraulicas' },
  { label: 'Motores Hidráulicos', to: '/motores-hidraulicos' },
  { label: 'Cilindros Hidráulicos', to: '/cilindros-hidraulicos' },
  { label: 'Válvulas Hidráulicas', to: '/valvulas-hidraulicas' },
  { label: 'Acumuladores Hidráulicos', to: '/acumuladores-hidraulicos' },
  { label: 'Manômetros e Medidores', to: '/manometros-medidores' },
  { label: 'Válvulas de Controle', to: '/valvulas-de-controle' },
  { label: 'Acessórios e Conexões', to: '/acessorios-conexoes' },
];

const servicosLinks = [
  { label: 'Mecânica Industrial', to: '/mecanica-industrial' },
  { label: 'Hidráulica Industrial', to: '/hidraulica-industrial' },
  { label: 'Caldeiraria e Solda', to: '/caldeiraria-solda' },
  { label: 'Lubrificação Industrial', to: '/lubrificacao-industrial' },
];

const casesLinks = [
  { label: 'Todos os Cases', to: '/cases' },
  { label: 'Cases de Serviços de Campo', to: '/cases?filter=campo' },
  { label: 'Cases de Oficina Especializada', to: '/cases?filter=oficina' },
];

interface DropdownMenuProps {
  label: string;
  links: { label: string; to: string }[];
  isActive: boolean;
  open: boolean;
  setOpen: (v: boolean) => void;
  mainTo: string;
  activeCheck: (path: string) => boolean;
}

function DropdownMenu({ label, links, isActive, open, setOpen, mainTo, activeCheck }: DropdownMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setOpen]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        className={`text-sm hover:text-gp-yellow transition-colors duration-300 font-medium uppercase tracking-wider whitespace-nowrap cursor-pointer flex items-center gap-1 ${
          isActive ? 'text-gp-yellow' : 'text-gray-300'
        }`}
      >
        {label}
        <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-300 ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-gp-graphite border border-gp-graphite-lighter rounded-lg shadow-xl py-2 z-50"
        >
          <Link
            to={mainTo}
            onClick={() => setOpen(false)}
            className={`block px-4 py-2 text-sm font-semibold border-b border-gp-graphite-lighter hover:bg-gp-graphite-lighter transition-colors duration-200 ${
              activeCheck(mainTo) ? 'text-gp-yellow' : 'text-gray-200 hover:text-gp-yellow'
            }`}
          >
            {label}
          </Link>
          {links.map((link) => (
            <Link
              key={link.to + link.label}
              to={link.to}
              onClick={() => setOpen(false)}
              className={`block px-4 py-2 text-sm hover:bg-gp-graphite-lighter transition-colors duration-200 ${
                activeCheck(link.to) ? 'text-gp-yellow' : 'text-gray-300 hover:text-gp-yellow'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

interface MobileDropdownMenuProps {
  label: string;
  mainTo: string;
  links: { label: string; to: string }[];
  open: boolean;
  setOpen: (v: boolean) => void;
  activeCheck: (path: string) => boolean;
  onNavigate: () => void;
}

function MobileDropdownMenu({ label, mainTo, links, open, setOpen, activeCheck, onNavigate }: MobileDropdownMenuProps) {
  return (
    <div>
      <Link
        to={mainTo}
        onClick={() => { onNavigate(); setOpen(false); }}
        className={`block font-medium uppercase tracking-wider py-2 ${
          activeCheck(mainTo) ? 'text-gp-yellow' : 'text-gray-300 hover:text-gp-yellow'
        }`}
      >
        {label}
      </Link>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between font-medium uppercase tracking-wider py-2 text-gray-300 hover:text-gp-yellow"
      >
        <span className="text-xs text-gray-500">Categorias</span>
        <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center transition-transform duration-300 ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && (
        <div className="pl-4 space-y-1 border-l-2 border-gp-yellow/30 ml-2 mt-1">
          {links.map((link) => (
            <Link
              key={link.to + link.label}
              to={link.to}
              onClick={() => { onNavigate(); setOpen(false); }}
              className={`block py-2 text-sm ${
                activeCheck(link.to) ? 'text-gp-yellow' : 'text-gray-400 hover:text-gp-yellow'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [oficinaOpen, setOficinaOpen] = useState(false);
  const [mobileOficinaOpen, setMobileOficinaOpen] = useState(false);
  const [duraflowOpen, setDuraflowOpen] = useState(false);
  const [mobileDuraflowOpen, setMobileDuraflowOpen] = useState(false);
  const [servicosOpen, setServicosOpen] = useState(false);
  const [mobileServicosOpen, setMobileServicosOpen] = useState(false);
  const [casesOpen, setCasesOpen] = useState(false);
  const [mobileCasesOpen, setMobileCasesOpen] = useState(false);
  const duraflowRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (duraflowRef.current && !duraflowRef.current.contains(e.target as Node)) {
        setDuraflowOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setOficinaOpen(false);
    setDuraflowOpen(false);
    setServicosOpen(false);
    setCasesOpen(false);
    setMobileOpen(false);
    setMobileOficinaOpen(false);
    setMobileDuraflowOpen(false);
    setMobileServicosOpen(false);
    setMobileCasesOpen(false);
  }, [location]);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname === '/') {
      const el = document.getElementById('forca-industrial');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
    }
  };

  const isActivePage = (path: string) => {
    if (path.includes('?')) {
      return location.pathname === path.split('?')[0];
    }
    return location.pathname === path;
  };
  const isDuraflowPage = duraflowLinks.some((l) => location.pathname === l.to);
  const isOficinaPage = location.pathname === '/oficina' || oficinaLinks.some((l) => location.pathname === l.to);
  const isServicosPage = location.pathname === '/servicos-de-campo' || servicosLinks.some((l) => location.pathname === l.to);
  const isCasesPage = location.pathname === '/cases' || location.pathname.startsWith('/case-') || location.pathname.startsWith('/cases/');

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || isDuraflowPage || isOficinaPage || isServicosPage || isCasesPage || location.pathname !== '/'
          ? 'bg-gp-graphite/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-24">
          <a
            href="/"
            onClick={handleLogoClick}
            className="flex items-center gap-3 cursor-pointer flex-shrink-0"
          >
            <img
              src={ASSETS.logoPilar}
              alt="Logo Grupo Pilar - Manutenção Industrial e Hidráulica Industrial"
              className="h-20 w-auto object-contain"
            />
          </a>

          <div className="hidden lg:flex items-center gap-8 xl:gap-10">
            <DropdownMenu
              label="Oficina Especializada"
              links={oficinaLinks}
              isActive={isOficinaPage}
              open={oficinaOpen}
              setOpen={setOficinaOpen}
              mainTo="/oficina"
              activeCheck={isActivePage}
            />

            <div ref={duraflowRef} className="relative">
              <DropdownMenu
                label="Equipamentos"
                links={duraflowLinks}
                isActive={isActivePage('/equipamentos-e-componentes') || isDuraflowPage}
                open={duraflowOpen}
                setOpen={setDuraflowOpen}
                mainTo="/equipamentos-e-componentes"
                activeCheck={isActivePage}
              />
            </div>

            <DropdownMenu
              label="Serviços de Campo"
              links={servicosLinks}
              isActive={isServicosPage}
              open={servicosOpen}
              setOpen={setServicosOpen}
              mainTo="/servicos-de-campo"
              activeCheck={isActivePage}
            />

            <Link
              to="/locacao-equipamentos-industriais"
              className={`text-sm hover:text-gp-yellow transition-colors duration-300 font-medium uppercase tracking-wider whitespace-nowrap ${
                isActivePage('/locacao-equipamentos-industriais') ? 'text-gp-yellow' : 'text-gray-300'
              }`}
            >
              Locação
            </Link>

            <DropdownMenu
              label="Cases"
              links={casesLinks}
              isActive={isCasesPage}
              open={casesOpen}
              setOpen={setCasesOpen}
              mainTo="/cases"
              activeCheck={isActivePage}
            />

            <Link
              to="/contato"
              className={`text-sm hover:text-gp-yellow transition-colors duration-300 font-medium uppercase tracking-wider whitespace-nowrap ${
                isActivePage('/contato') ? 'text-gp-yellow' : 'text-gray-300'
              }`}
            >
              Contato
            </Link>

            <Link
              to="/trabalhe-conosco"
              className={`text-sm hover:text-gp-yellow transition-colors duration-300 font-medium uppercase tracking-wider whitespace-nowrap ${
                isActivePage('/trabalhe-conosco') ? 'text-gp-yellow' : 'text-gray-300'
              }`}
            >
              Trabalhe Conosco
            </Link>
          </div>

          <button
            className="lg:hidden text-white p-2 w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-gp-graphite border-t border-gp-graphite-lighter max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-6 space-y-2">
            <MobileDropdownMenu
              label="Oficina Especializada"
              mainTo="/oficina"
              links={oficinaLinks}
              open={mobileOficinaOpen}
              setOpen={setMobileOficinaOpen}
              activeCheck={isActivePage}
              onNavigate={() => setMobileOpen(false)}
            />

            <div>
              <Link
                to="/equipamentos-e-componentes"
                onClick={() => setMobileOpen(false)}
                className={`block font-medium uppercase tracking-wider py-2 ${
                  isActivePage('/equipamentos-e-componentes') ? 'text-gp-yellow' : 'text-gray-300 hover:text-gp-yellow'
                }`}
              >
                Equipamentos
              </Link>
              <button
                onClick={() => setMobileDuraflowOpen(!mobileDuraflowOpen)}
                className="w-full flex items-center justify-between font-medium uppercase tracking-wider py-2 text-gray-300 hover:text-gp-yellow"
              >
                <span className="text-xs text-gray-500">Produtos</span>
                <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center transition-transform duration-300 ${mobileDuraflowOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileDuraflowOpen && (
                <div className="pl-4 space-y-1 border-l-2 border-gp-yellow/30 ml-2 mt-1">
                  {duraflowLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => { setMobileOpen(false); setMobileDuraflowOpen(false); }}
                      className={`block py-2 text-sm ${
                        location.pathname === link.to ? 'text-gp-yellow' : 'text-gray-400 hover:text-gp-yellow'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <MobileDropdownMenu
              label="Serviços de Campo"
              mainTo="/servicos-de-campo"
              links={servicosLinks}
              open={mobileServicosOpen}
              setOpen={setMobileServicosOpen}
              activeCheck={isActivePage}
              onNavigate={() => setMobileOpen(false)}
            />

            <Link
              to="/locacao-equipamentos-industriais"
              onClick={() => setMobileOpen(false)}
              className={`block font-medium uppercase tracking-wider py-2 ${
                isActivePage('/locacao-equipamentos-industriais') ? 'text-gp-yellow' : 'text-gray-300 hover:text-gp-yellow'
              }`}
            >
              Locação
            </Link>

            <MobileDropdownMenu
              label="Cases"
              mainTo="/cases"
              links={casesLinks}
              open={mobileCasesOpen}
              setOpen={setMobileCasesOpen}
              activeCheck={isActivePage}
              onNavigate={() => setMobileOpen(false)}
            />

            <Link
              to="/contato"
              onClick={() => setMobileOpen(false)}
              className={`block font-medium uppercase tracking-wider py-2 ${
                isActivePage('/contato') ? 'text-gp-yellow' : 'text-gray-300 hover:text-gp-yellow'
              }`}
            >
              Contato
            </Link>

            <Link
              to="/trabalhe-conosco"
              onClick={() => setMobileOpen(false)}
              className={`block font-medium uppercase tracking-wider py-2 ${
                isActivePage('/trabalhe-conosco') ? 'text-gp-yellow' : 'text-gray-300 hover:text-gp-yellow'
              }`}
            >
              Trabalhe Conosco
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}